import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Table, Button, Modal, Form, Input, Space, Typography, Card, App, 
  Select, Tag, Switch, Tooltip, Alert, Dropdown, Popover, Checkbox 
} from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LockOutlined, UnlockOutlined, SearchOutlined, SwapOutlined, CopyOutlined, MoreOutlined, InfoCircleOutlined, RollbackOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';
import systemConfigService, { 
  type SystemConfig, 
  type CreateSystemConfigRequest, 
  type UpdateSystemConfigRequest, 
  type ConfigConsumer,
  type ConfigValueType
} from '../../api/systemConfigService';
import { ConfigRevealModal } from '../../components/ConfigReveal/ConfigRevealModal';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** Format updated_at - handles BigQuery format { value: string } or plain ISO string. Shows UTC, 24h. */
function formatUpdateDatetime(val: string | { value?: string } | null | undefined): string {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return '-';
  const date = new Date(str);
  return isNaN(date.getTime()) ? '-' : `${date.toLocaleString(undefined, { timeZone: 'UTC', hour12: false })} UTC`;
}

/** Extract timestamp for sorting - handles BigQuery format */
function getTimestampForSort(val: string | { value?: string } | null | undefined): number {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return 0;
  const date = new Date(str);
  return isNaN(date.getTime()) ? 0 : date.getTime();
}

// Consumer options for the multi-select dropdown
const CONSUMER_OPTIONS: { value: ConfigConsumer; label: string; color: string }[] = [
  { value: 'backend', label: 'Backend', color: 'blue' },
  { value: 'jobs', label: 'Jobs', color: 'purple' },
  { value: 'client', label: 'Client', color: 'green' },
  { value: 'scheduler', label: 'Scheduler', color: 'orange' },
  { value: 'notification-service', label: 'Notification Service', color: 'cyan' },
  { value: 'general', label: 'General', color: 'gold' },
];

// Value type options
const VALUE_TYPE_OPTIONS: { value: ConfigValueType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'json', label: 'JSON' },
];

// Tag colors for consumers
const CONSUMER_COLORS: Record<ConfigConsumer, string> = {
  backend: 'blue',
  jobs: 'purple',
  client: 'green',
  scheduler: 'orange',
  'notification-service': 'cyan',
  general: 'gold',
};

// Tag colors for value types
const VALUE_TYPE_COLORS: Record<ConfigValueType, string> = {
  string: 'default',
  number: 'cyan',
  boolean: 'geekblue',
  json: 'magenta',
};

interface SystemConfigFormValues {
  config_key: string;
  config_value?: string;
  consumers: ConfigConsumer[];
  sensitive: boolean;
  description?: string;
  value_type: ConfigValueType;
}

export const SystemConfigManagement = () => {
  const { message } = App.useApp();
  const { authState } = useAuth();
  const token = authState.token;
  const [configs, setConfigs] = useState<SystemConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SystemConfig | null>(null);
  const [form] = Form.useForm<SystemConfigFormValues>();
  
  // Rename modal state
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renamingConfig, setRenamingConfig] = useState<SystemConfig | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameForm] = Form.useForm<{ new_key: string }>();

  // Include soft-deleted configs in the list (for management UI)
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [revealForEdit, setRevealForEdit] = useState(false);

  // Fetch all system configurations
  const fetchConfigs = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const data = await systemConfigService.getAllConfigs(token, undefined, includeDeleted);
      setConfigs(data);
    } catch (error) {
      message.error('Failed to fetch system configurations');
      console.error('Error fetching system configurations:', error);
    } finally {
      setLoading(false);
    }
  }, [message, token, includeDeleted]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Auto-refresh when system_config changes (e.g., from another user/tab)
  const metadataTimestamps = useCollectionFreshness(['system_config'], {
    interval: 30_000,
    enabled: true,
  });
  const lastConfigTsRef = useRef<string | null>(null);

  useEffect(() => {
    const ts = metadataTimestamps['system_config'];
    if (!ts) return;
    if (!lastConfigTsRef.current) {
      lastConfigTsRef.current = ts;
      return;
    }
    if (ts === lastConfigTsRef.current) return;
    lastConfigTsRef.current = ts;
    fetchConfigs();
  }, [metadataTimestamps, fetchConfigs]);

  // Handle create/edit modal (don't allow editing deleted configs)
  const handleOpenModal = (config?: SystemConfig) => {
    if (config?.deleted) return;
    if (config) {
      setEditingConfig(config);
      form.setFieldsValue({
        config_key: config.config_key,
        config_value: undefined,
        consumers: config.consumers || ['backend'],
        sensitive: config.sensitive,
        description: config.description ?? undefined,
        value_type: config.value_type,
      });
    } else {
      setEditingConfig(null);
      form.resetFields();
      // Set default values for new config
      form.setFieldsValue({
        consumers: ['backend'],
        sensitive: false,
        value_type: 'string',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!token) {
        message.error('Authentication required');
        return;
      }

      setSaving(true);

      if (editingConfig) {
        const updates: UpdateSystemConfigRequest = {
          consumers: values.consumers,
          sensitive: values.sensitive,
          description: values.description || undefined,
          value_type: values.value_type,
        };
        if (values.config_value !== undefined && values.config_value !== '') {
          updates.config_value = values.config_value;
        }

        await systemConfigService.updateConfig(editingConfig.config_key, updates, token);
        message.success('Configuration updated successfully');
      } else {
        if (!values.config_value) {
          message.error('Value is required');
          return;
        }
        const configData: CreateSystemConfigRequest = {
          config_key: values.config_key,
          config_value: values.config_value,
          consumers: values.consumers,
          sensitive: values.sensitive ?? false,
          description: values.description || undefined,
          value_type: values.value_type ?? 'string',
        };

        await systemConfigService.createConfig(configData, token);
        message.success('Configuration created successfully');
      }

      handleCloseModal();
      fetchConfigs();
    } catch (error) {
      console.error('Error saving configuration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save configuration';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configKey: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await systemConfigService.deleteConfig(configKey, token);
      message.success('Configuration soft-deleted successfully');
      fetchConfigs();
    } catch (error) {
      message.error('Failed to delete configuration');
      console.error('Error deleting configuration:', error);
    }
  };

  const handleRestore = async (configKey: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await systemConfigService.setConfigDeleted(configKey, false, token);
      message.success('Configuration restored successfully');
      fetchConfigs();
    } catch (error) {
      message.error('Failed to restore configuration');
      console.error('Error restoring configuration:', error);
    }
  };

  // Duplicate handler
  const handleDuplicate = (config: SystemConfig) => {
    setEditingConfig(null);
    form.setFieldsValue({
      config_key: `${config.config_key}_copy`,
      config_value: undefined,
      consumers: config.consumers || ['backend'],
      sensitive: config.sensitive,
      description: config.description ?? undefined,
      value_type: config.value_type,
    });
    setIsModalOpen(true);
  };

  // Rename handlers
  const handleOpenRenameModal = (config: SystemConfig) => {
    setRenamingConfig(config);
    renameForm.setFieldsValue({ new_key: config.config_key });
    setIsRenameModalOpen(true);
  };

  const handleCloseRenameModal = () => {
    setIsRenameModalOpen(false);
    setRenamingConfig(null);
    renameForm.resetFields();
  };

  const handleRename = async () => {
    try {
      const values = await renameForm.validateFields();

      if (!token || !renamingConfig) {
        message.error('Authentication required');
        return;
      }

      const newKey = values.new_key.trim();

      if (newKey === renamingConfig.config_key) {
        message.warning('New key is the same as the current key');
        return;
      }

      setRenaming(true);

      await systemConfigService.renameConfig(renamingConfig.config_key, newKey, token);
      message.success(`Configuration renamed to ${newKey}`);
      handleCloseRenameModal();
      fetchConfigs();
    } catch (error) {
      console.error('Error renaming configuration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename configuration';
      message.error(errorMessage);
    } finally {
      setRenaming(false);
    }
  };

  const columns: ColumnsType<SystemConfig> = [
    {
      title: 'Key',
      dataIndex: 'config_key',
      key: 'config_key',
      sorter: (a, b) => a.config_key.localeCompare(b.config_key),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search key..."
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => { clearFilters?.(); confirm(); }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.config_key.toLowerCase().includes((value as string).toLowerCase()),
      render: (key: string, record) => {
        const popoverContent = (
          <div style={{ maxWidth: 320 }}>
            <div style={{ 
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px 8px 0 0',
              margin: '-12px -16px 12px -16px'
            }}>
              <Text code style={{ 
                fontSize: 13, 
                background: 'rgba(255,255,255,0.2)', 
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 4
              }}>
                {key}
              </Text>
            </div>
            {record.description ? (
              <div style={{ color: '#595959', lineHeight: 1.6 }}>
                {record.description}
              </div>
            ) : (
              <Text type="secondary" italic>No description provided</Text>
            )}
          </div>
        );

        return (
          <Space size={4}>
            <Text code style={{ fontSize: 12 }}>{key}</Text>
            {record.deleted && <Tag color="red">Deleted</Tag>}
            <Popover 
              content={popoverContent} 
              trigger="hover"
              placement="right"
              styles={{ root: { padding: '12px 16px' } }}
            >
              <InfoCircleOutlined style={{ color: record.description ? '#1890ff' : '#bfbfbf', cursor: 'pointer', fontSize: 12 }} />
            </Popover>
          </Space>
        );
      },
    },
    {
      title: 'Value',
      dataIndex: 'config_value',
      key: 'config_value',
      ellipsis: true,
      render: (_value: string | null, record) => (
        <Space size={8}>
          <Tooltip title="Value hidden. Click Show and confirm your password to view.">
            <Text type="secondary">
              {record.sensitive ? <LockOutlined /> : null} ••••••••
            </Text>
          </Tooltip>
          {!record.deleted && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setRevealForEdit(false);
                setRevealKey(record.config_key);
              }}
              style={{ padding: 0 }}
            >
              Show
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Consumers',
      dataIndex: 'consumers',
      key: 'consumers',
      width: 400,
      filters: CONSUMER_OPTIONS.map(c => ({ text: c.label, value: c.value })),
      onFilter: (value, record) => (record.consumers || []).includes(value as ConfigConsumer),
      render: (consumers: ConfigConsumer[]) => (
        <Space size={[4, 4]} wrap>
          {(consumers || []).map((consumer) => (
            <Tag key={consumer} color={CONSUMER_COLORS[consumer]} style={{ margin: 0, fontSize: 11 }}>
              {consumer}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'value_type',
      key: 'value_type',
      width: 80,
      render: (valueType: ConfigValueType) => (
        <Tag color={VALUE_TYPE_COLORS[valueType]}>
          {valueType}
        </Tag>
      ),
    },
    {
      title: '',
      dataIndex: 'sensitive',
      key: 'sensitive',
      width: 60,
      sorter: (a, b) => Number(a.sensitive) - Number(b.sensitive),
      render: (sensitive: boolean) => (
        <Tooltip title={sensitive ? 'Never exposed to client' : 'Can be exposed to client'}>
          {sensitive ? (
            <LockOutlined style={{ color: '#ff4d4f' }} />
          ) : (
            <UnlockOutlined style={{ color: '#52c41a' }} />
          )}
        </Tooltip>
      ),
      filters: [
        { text: 'Sensitive', value: true },
        { text: 'Not Sensitive', value: false },
      ],
      onFilter: (value, record) => record.sensitive === value,
    },
    {
      title: 'Updated',
      key: 'update_info',
      width: 180,
      sorter: (a, b) => getTimestampForSort(a.updated_at) - getTimestampForSort(b.updated_at),
      defaultSortOrder: 'descend',
      render: (_, record) => (
        <Space orientation="vertical" size={0} style={{ fontSize: 11 }}>
          {record.updated_by && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.updated_by}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 10 }}>
            {formatUpdateDatetime(record.updated_at)}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          ...(record.deleted
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Restore',
                  onClick: () => handleRestore(record.config_key),
                },
              ]
            : [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () => handleOpenModal(record),
                },
                {
                  key: 'duplicate',
                  icon: <CopyOutlined />,
                  label: 'Duplicate',
                  onClick: () => handleDuplicate(record),
                },
                {
                  key: 'rename',
                  icon: <SwapOutlined />,
                  label: 'Rename',
                  onClick: () => handleOpenRenameModal(record),
                },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Delete',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Soft-delete Configuration',
                      content: (
                        <div>
                          Are you sure you want to soft-delete <Text code>{record.config_key}</Text>?
                          <br />
                          <Text type="secondary">The configuration can be restored later from the list.</Text>
                        </div>
                      ),
                      okText: 'Yes, Delete',
                      cancelText: 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.config_key),
                    });
                  },
                },
              ]),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={2} style={{ margin: 0 }}>System Configuration</Title>
          <Space>
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            >
              Include deleted
            </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Add Configuration
          </Button>
          </Space>
        </div>

        <Alert
          title="System Configuration Management"
          description={
            <span>
              Manage centralized configurations for backend, jobs, and client. 
              <strong> Values are hidden</strong> until you click Show and confirm your password.
              <strong> Sensitive</strong> configurations are never exposed to the client API.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={configs}
            rowKey="config_key"
            loading={loading}
            pagination={false}
            scroll={{ y: 'calc(100vh - 280px)' }}
            size="small"
            footer={() => (
              <div style={{ textAlign: 'right', color: '#8c8c8c', fontSize: 12 }}>
                {configs.length} configuration{configs.length !== 1 ? 's' : ''}
              </div>
            )}
          />
        </div>
      </Card>

      {/* Modal */}
      <Modal
        title={editingConfig ? 'Edit Configuration' : 'Add Configuration'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={560}
        okText={editingConfig ? 'Update' : 'Create'}
        confirmLoading={saving}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          {/* Config Key */}
          <Form.Item
            name="config_key"
            label="Configuration Key"
            rules={[
              { required: true, message: 'Configuration key is required' },
              { 
                pattern: /^[a-zA-Z][a-zA-Z0-9._]*$/, 
                message: 'Must start with a letter and contain only letters, numbers, dots, and underscores' 
              },
            ]}
            tooltip="Unique identifier (e.g., 'email.mailgun_domain', 'ai_service.url')"
          >
            <Input 
              placeholder="e.g., email.mailgun_domain" 
              disabled={!!editingConfig}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          {/* Consumers multi-select */}
          <Form.Item
            name="consumers"
            label="Consumers"
            rules={[{ required: true, message: 'At least one consumer is required' }]}
            tooltip="Select all systems that use this configuration"
          >
            <Select
              mode="multiple"
              placeholder="Select consumers..."
              options={CONSUMER_OPTIONS}
              optionRender={(option) => (
                <Space>
                  <Tag color={CONSUMER_COLORS[option.value as ConfigConsumer]} style={{ margin: 0 }}>
                    {option.label}
                  </Tag>
                </Space>
              )}
              tagRender={(props) => (
                <Tag 
                  color={CONSUMER_COLORS[props.value as ConfigConsumer]} 
                  closable={props.closable}
                  onClose={props.onClose}
                  style={{ marginRight: 3 }}
                >
                  {props.label}
                </Tag>
              )}
            />
          </Form.Item>

          {/* Value Type */}
          <Form.Item
            name="value_type"
            label="Value Type"
            rules={[{ required: true, message: 'Value type is required' }]}
            tooltip="How the value should be parsed by the application"
          >
            <Select options={VALUE_TYPE_OPTIONS} />
          </Form.Item>

          {/* Config Value - Dynamic based on value_type */}
          {editingConfig && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setRevealForEdit(true);
                setRevealKey(editingConfig.config_key);
              }}
              style={{ padding: 0, marginBottom: 8 }}
            >
              Load current value
            </Button>
          )}
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.value_type !== curr.value_type}>
            {({ getFieldValue }) => {
              const valueType = getFieldValue('value_type') || 'string';
              const valueRequired = !editingConfig;
              const keepHint = editingConfig ? 'Leave blank to keep the current value' : undefined;
              
              if (valueType === 'boolean') {
                return (
                  <Form.Item
                    name="config_value"
                    label="Value"
                    rules={valueRequired ? [{ required: true, message: 'Value is required' }] : []}
                    tooltip={keepHint || 'Boolean value: true or false'}
                  >
                    <Select
                      allowClear={!!editingConfig}
                      options={[
                        { value: 'true', label: 'true' },
                        { value: 'false', label: 'false' },
                      ]}
                      placeholder={editingConfig ? 'Leave blank to keep current' : 'Select true or false'}
                    />
                  </Form.Item>
                );
              }
              
              if (valueType === 'number') {
                return (
                  <Form.Item
                    name="config_value"
                    label="Value"
                    rules={[
                      ...(valueRequired ? [{ required: true, message: 'Value is required' }] : []),
                      {
                        validator: (_: unknown, value: string) => {
                          if (value === '' || value === undefined) return Promise.resolve();
                          if (isNaN(Number(value))) {
                            return Promise.reject('Must be a valid number');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                    tooltip={keepHint || 'Numeric value (integer or decimal)'}
                  >
                    <Input
                      placeholder={editingConfig ? 'Leave blank to keep current' : 'e.g., 30000'}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </Form.Item>
                );
              }
              
              if (valueType === 'json') {
                return (
                  <Form.Item
                    name="config_value"
                    label="Value"
                    rules={[
                      ...(valueRequired ? [{ required: true, message: 'Value is required' }] : []),
                      {
                        validator: (_: unknown, value: string) => {
                          if (!value) return Promise.resolve();
                          try {
                            JSON.parse(value);
                            return Promise.resolve();
                          } catch {
                            return Promise.reject('Must be valid JSON');
                          }
                        }
                      }
                    ]}
                    tooltip={keepHint || 'Must be valid JSON'}
                  >
                    <TextArea 
                      placeholder={editingConfig ? 'Leave blank to keep current' : '{"key": "value"}'}
                      rows={4}
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </Form.Item>
                );
              }
              
              return (
                <Form.Item
                  name="config_value"
                  label="Value"
                  rules={valueRequired ? [{ required: true, message: 'Value is required' }] : []}
                  tooltip={keepHint}
                >
                  <TextArea 
                    placeholder={editingConfig ? 'Leave blank to keep current' : 'Configuration value'}
                    rows={2}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label="Description"
            tooltip="Human-readable description for documentation"
          >
            <Input placeholder="e.g., Mailgun domain for sending emails" />
          </Form.Item>

          {/* Sensitive Toggle */}
          <Form.Item
            name="sensitive"
            label="Sensitive"
            valuePropName="checked"
            tooltip="If enabled, this configuration will NEVER be exposed to the client API"
          >
            <Switch 
              checkedChildren={<LockOutlined />}
              unCheckedChildren={<UnlockOutlined />}
            />
          </Form.Item>

          {/* Warning for client consumer */}
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.consumers !== curr.consumers || prev.sensitive !== curr.sensitive}>
            {({ getFieldValue }) => {
              const consumers: ConfigConsumer[] = getFieldValue('consumers') || [];
              const sensitive = getFieldValue('sensitive');
              const hasClientConsumer = consumers.includes('client');
              
              if (hasClientConsumer && sensitive) {
                return (
                  <Alert
                    title="This configuration is marked as sensitive but has 'client' as a consumer"
                    description="It will NOT be exposed to the client API because 'sensitive' is enabled."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
              
              if (hasClientConsumer && !sensitive) {
                return (
                  <Alert
                    title="This configuration will be publicly accessible"
                    description="Configurations with 'client' consumer and sensitive=false are exposed via /api/config/client"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
              
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Rename Modal */}
      <Modal
        title="Rename Configuration"
        open={isRenameModalOpen}
        onCancel={handleCloseRenameModal}
        onOk={handleRename}
        okText="Rename"
        confirmLoading={renaming}
        destroyOnHidden
        width={500}
      >
        <Form form={renameForm} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title={
              <span>
                Renaming <Text code>{renamingConfig?.config_key}</Text>
              </span>
            }
            description="Enter the new key name below. Make sure to update any code that references this configuration."
          />

          <Form.Item
            name="new_key"
            label="New Key"
            rules={[
              { required: true, message: 'Key is required' },
              { 
                pattern: /^[a-zA-Z][a-zA-Z0-9._]*$/,
                message: 'Key must start with a letter and contain only letters, numbers, dots, and underscores'
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const exists = configs.some(
                    c => c.config_key === value.trim() && c.config_key !== renamingConfig?.config_key
                  );
                  if (exists) {
                    return Promise.reject(new Error('A configuration with this key already exists'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              placeholder="e.g., email.mailgun_api_key" 
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ConfigRevealModal
        configKey={revealKey}
        open={!!revealKey}
        showRevealedValue={!revealForEdit}
        onRevealed={revealForEdit ? (value) => form.setFieldsValue({ config_value: value }) : undefined}
        onClose={() => {
          setRevealKey(null);
          setRevealForEdit(false);
        }}
      />
    </div>
  );
};

export default SystemConfigManagement;

